import styled from "@emotion/styled/macro";
import React, { useState, useEffect, useCallback } from "react";
import ReactGA from "react-ga";
import debounce from "lodash/debounce";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  withRouter,
  RouteComponentProps
} from "react-router-dom";

import Header from "../common/Header";
import BobbleContent from "../BobbleContent";
import NoBobbleContent from "../NoBobbleContent";
//import SignInDialog from "../Dialogs/SignInDialog";
import ApiService, { IUser, ICachedData } from "../ApiService";
import MyCollection from "../MyCollection";
import GetMyBonusDialog from "../Dialogs/GetMyBonusDialog";

import collectionBg from "../res/bg_wall_repeating.jpg";
import CarouselItem from "../MyCollection/CarouselItem";
import usePrevious from "../common/usePrevious";

import PlayFab from "../Playfab/PlayFab";
import { singInAnonymous } from "../firebase";

import MainIndex, { bobbleHeadID } from "../index";

export interface IBobblehead {
  id: string;
  name: string;
  modelUrl: string;
  primaryLogoUrl: string;
  baseSideTextureUrl: string;
  baseTextureUrl: string;
  scratchCoverUrl: string;
  thumbnailUrl: string;
  silhouetteUrl: string;
  eventId: string;
  modelScaleFactor: number;
  modelOffsetY: number;
  baseScaleFactor: number;
  baseOffsetY: number;
}

export interface IEvent {
  id: string;
  bobbleheadIds: string[];
  title: string;
  subtitle: string;
}

interface IProps {
  showPlaceholder?: boolean;
  initialData: ICachedData;
  bobblehead: IBobblehead | null;
}

const AppRoot = styled<"div", { isCollectionPage: boolean }>("div")`
  height: 100%;
  background: ${({ isCollectionPage }) =>
    isCollectionPage
      ? `url(${collectionBg}) repeat center top transparent`
      : "none"};
  display: flex;
  flex-direction: column;
`;

let collectedBobbleheadIds: string[];
let eventIds: string[] = [];
let cachedData: ICachedData;
let collectedSet: Set<string>;
let collectedSortedBobbleheadIds: string[] = [];
let scratchedBobbleheadIds: string [] = [];

export function SetCollection()
{
  collectedBobbleheadIds = PlayFab.GetBobbleID();
  collectedSet = new Set(collectedBobbleheadIds);
  collectedSet.forEach(function(id) {
    collectedSortedBobbleheadIds.push(id);
  })
  ApiService.setNewPrizeImage(PlayFab.GetPrizeString(PlayFab.GetCurrentBobble()));
}

export function SetEventData(data: ICachedData) {
  cachedData = data;
}

function App({
  bobblehead,
  initialData,
  location,
  showPlaceholder
}: IProps & RouteComponentProps) {

  let link: HTMLLinkElement | null = document.querySelector('link[rel="shortcut icon"]');
  if(link) {
    if(bobbleHeadID === "M0") {
      link.href = "../../favicon-32x32_mets.png";
    }
  }

  const [hasBobbledHead, setHasBobbledHead] = useState(false);
  const [hasRotatedModel, setHasRotatedModel] = useState(false);
  const [isCollectingBobblehead, setIsCollectingBobblehead] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  cachedData = initialData;
  const previousLocation = usePrevious(location);

  if(bobblehead) {
    PlayFab.SetCurrentBobble(bobblehead.id);
    ApiService.setNewPrizeImage(PlayFab.GetPrizeString(PlayFab.GetCurrentBobble()));
  }

  const { userId }: { userId: string | null } = {
    userId: null
    // TODO: Get this from somewhere
  };
  const [signedInUser, setSignedInUser] = useState<IUser | null>(
    userId
      ? {
          userId
        }
      : null
  );

  const [loginOnce, setLoginOnce] = useState(Boolean);

  const isCollectionPage =
    location.pathname.endsWith("collection") ||
    location.pathname.endsWith("collection/");

  const updateSignedInUser = (patch: Partial<IUser>) => {
    setSignedInUser((user: IUser | null) => {
      if (!user) {
        throw new Error("Cannot update user when not signed in");
      }
      return { ...user, ...patch };
    });
  };

  useEffect(() => {
    if (location !== previousLocation) {
      ReactGA.pageview(location.pathname);
    }
  }, [location, previousLocation]);

  useEffect(() => {
    ApiService.lookup({
      bobbleheadIds: collectedBobbleheadIds || [],
      eventIds
    }).then((data: ICachedData) => {
      SetEventData(data);
    });
  }, [collectedBobbleheadIds, eventIds]);

  const handleLogin = useCallback(
    (response: any) => {
      const { userId } = response;
      if (userId) {
        setSignedInUser({
          userId
        });
        //Not relevant with new backend and flow
        if (bobblehead && bobblehead.eventId === "bc2019") {
          //console.log("From original code");
        }
      }
    },
    [bobblehead]
  );

  const showSignInDialog = () => {
    setIsSigningIn(true);
  };

  const onHeadClicked = () => {
    setHasBobbledHead(true);
  };

  const trackBobbleRotation = debounce(() => {
    ReactGA.event({
      category: "bobblehead interaction",
      action: "rotate bobblehead"
    });
  }, 500);

  const onModelRotated = () => {
    if (!hasRotatedModel) {
      setHasRotatedModel(true);
    }
    trackBobbleRotation();
  };

  const onSwipedToReveal = () => {
    ReactGA.event({
      category: "bobblehead interaction",
      action: "view bonus",
      label: "via swipe"
    });
  };

  //PLAYFAB LOGIN (ANONYMOUS)
  if(!loginOnce)
  {
    singInAnonymous();
    PlayFab.DoExampleLoginWithCustomID("anonymous");
    let newUser: IUser = { userId: "anonymous", email: "anon@provider.com"};
    handleLogin(newUser);
    setLoginOnce(true);
  }
  //

  const collected = collectedBobbleheadIds || [];
  const scratched = scratchedBobbleheadIds || [];

  return (
    <AppRoot isCollectionPage={isCollectionPage}>

      <Header
        bobblehead={bobblehead}
        cachedData={cachedData}
        isSignedIn={!!signedInUser}
        showPlaceholder={showPlaceholder}
        showSignInDialog={showSignInDialog}
      />
      <Switch>
        <Route
          path="/"
          exact
          render={() => {
            if (bobblehead) {
              const collectedIndex = collected.indexOf(bobblehead.id);
              if(collectedBobbleheadIds && collectedBobbleheadIds.includes(bobblehead.id)) {
                bobblehead.scratchCoverUrl = "";
              }
              return (
                <BobbleContent
                  dataInitialized={
                    !!collectedBobbleheadIds && !!scratchedBobbleheadIds
                  }
                  hasBobbledHead={hasBobbledHead}
                  hasRotatedModel={hasRotatedModel}
                  onHeadClicked={onHeadClicked}
                  onModelRotated={onModelRotated}
                  hasBobbleheads={collected.length > 0}
                  hasBobbleheadInCollection={collectedIndex >= 0}
                  isScratched={scratchedBobbleheadIds.includes(bobblehead.id)}
                  bobblehead={bobblehead}
                  signedInUser={signedInUser}
                  showSignInDialog={showSignInDialog}
                  isSigningIn={isSigningIn}
                  onGetMyBonusSuccess={(email) => {
                    updateSignedInUser({ email });
                  }}
                  onSwipedToReveal={onSwipedToReveal}
                  onScratchComplete={() => {
                    scratchedBobbleheadIds.push(bobblehead.id);
                    ReactGA.event({
                      category: "bonus",
                      action: "reveal"
                    });
                  }}
                />
              );
            } else {
              return <NoBobbleContent showPlaceholder={showPlaceholder} />;
            }
          }}
        />
        {signedInUser && (
          <Route
            path="/collection"
            exact
            render={props => (
              <MyCollection
                {...props}
                collectedBobbleheadIds={collected}
                cachedData={cachedData}
              />
            )}
          />
        )}
        {signedInUser && (
          <Route
            path="/collection/:bobbleheadId"
            render={props => {
              const { bobbleheadId } = props.match.params;
              return (
                <CarouselItem
                  {...props}
                  key={bobbleheadId}
                  hasBobbledHead={hasBobbledHead}
                  hasRotatedModel={hasRotatedModel}
                  onHeadClicked={onHeadClicked}
                  onModelRotated={onModelRotated}
                  collectedBobbleheadIds={collectedSortedBobbleheadIds}
                  isScratched={scratchedBobbleheadIds.includes(bobbleheadId)}
                  cachedData={cachedData}
                  signedInUser={signedInUser}
                  onSwipedToReveal={onSwipedToReveal}
                  onScratchComplete={() => {
                    scratchedBobbleheadIds.push(bobbleheadId.id)
                    ReactGA.event({
                      category: "bonus",
                      action: "reveal"
                    });
                  }}
                />
              );
            }}
          />
        )}
        <Redirect to="/" />
      </Switch>
      {isSigningIn && (
        <GetMyBonusDialog
          signedInUser={signedInUser!}
          bobblehead={bobblehead!}
          onClose={() => {
            setIsSigningIn(false);
          }}
          onSuccess={(logInResult) => {
            updateSignedInUser({email: logInResult});
            setIsSigningIn(false);
          }}
        />
      )}
    </AppRoot>
  );
}

const ConnectedApp = withRouter(App);

export default function BetterBobblehead(props: IProps) {
  return (
    <Router>
      <ConnectedApp {...props} />
    </Router>
  );
}
